/**
 * Live video over WebRTC with Cloudflare Stream Live: publishing a camera (WHIP) and playing it back (WHEP),
 * with no client library.
 *
 * Both protocols are a single HTTP exchange: POST our SDP offer as `application/sdp`, receive the SDP answer
 * with `201 Created` and a `Location` header naming the session resource, and DELETE that resource to end
 * the session. We don't trickle ICE: the offer goes out once gathering completes (or times out).
 */

const STUN_SERVER_URL = 'stun:stun.cloudflare.com:3478'
/** Longest we hold the offer back waiting for ICE gathering; it goes out with whatever was gathered by then */
const ICE_GATHERING_TIMEOUT_MS = 2000

export type TVideoConnectionState = RTCPeerConnectionState

/**
 * The video service refused the offer. Cloudflare answers anything but 201 when, for example, playback is
 * requested before the host is broadcasting, so callers treat this as "not available yet" and retry.
 */
export class VideoSessionHttpError extends Error {
    status: number

    constructor (status: number, responseText: string) {
        super(`Video session request failed with status ${status}${responseText ? `: ${responseText}` : ''}`)
        this.name = 'VideoSessionHttpError'
        this.status = status
    }
}

/** The stream key in a publish URL: `https://customer-<code>.cloudflarestream.com/<key>/webRTC/publish` */
const PUBLISH_KEY_PATTERN = /[^/"\s]+(?=\/webRTC\/publish)/g

/**
 * Hides the stream key in every publish URL (or session URL) in the text, so it can be logged.
 * Works on a single URL or on serialized JSON; other URLs pass through.
 */
export const redactVideoPublishKey = (text: string) => text.replace(PUBLISH_KEY_PATTERN, '[redacted]')

/**
 * Session Replay records request and response bodies, and the host console polls a snapshot that
 * carries `publishUrl`. Redacts stream keys from replay network events (rrweb custom events, type 5).
 */
export const redactReplayEventVideoPublishKey = <T>(event: T): T => {
    if ((event as { type?: unknown }).type !== 5) {
        return event
    }
    const serialized = JSON.stringify(event)
    const redacted = redactVideoPublishKey(serialized)
    return redacted === serialized ? event : JSON.parse(redacted) as T
}

const createPeerConnection = () => new RTCPeerConnection({
    iceServers: [{ urls: STUN_SERVER_URL }],
    bundlePolicy: 'max-bundle',
})

const waitForIceGathering = (peerConnection: RTCPeerConnection) => new Promise<void>(resolve => {
    if (peerConnection.iceGatheringState === 'complete') {
        resolve()
        return
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null
    const handleGatheringStateChange = () => {
        if (peerConnection.iceGatheringState === 'complete') {
            finish()
        }
    }
    const finish = () => {
        if (timeoutId) {
            clearTimeout(timeoutId)
        }
        peerConnection.removeEventListener('icegatheringstatechange', handleGatheringStateChange)
        resolve()
    }
    peerConnection.addEventListener('icegatheringstatechange', handleGatheringStateChange)
    timeoutId = setTimeout(finish, ICE_GATHERING_TIMEOUT_MS)
})

/** Sends our offer to a WHIP/WHEP endpoint, applies the answer, and returns the session resource URL */
const negotiateSession = async ({ peerConnection, endpointUrl }: {
    peerConnection: RTCPeerConnection
    endpointUrl: string
}) => {
    const offer = await peerConnection.createOffer()
    await peerConnection.setLocalDescription(offer)
    await waitForIceGathering(peerConnection)

    // The local description now carries the gathered candidates; the original offer doesn't
    const offerSdp = peerConnection.localDescription?.sdp
    if (!offerSdp) {
        throw new Error('negotiateSession: no local description after creating the offer')
    }

    const response = await fetch(endpointUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/sdp' },
        body: offerSdp,
    })
    if (response.status !== 201) {
        throw new VideoSessionHttpError(response.status, await response.text())
    }

    const answerSdp = await response.text()
    await peerConnection.setRemoteDescription({ type: 'answer', sdp: answerSdp })

    const sessionLocation = response.headers.get('Location')
    return sessionLocation ? new URL(sessionLocation, endpointUrl).href : null
}

/** Ends a session: closes the connection, then deletes the server's session resource. Safe to call twice. */
const createSessionStopper = ({ peerConnection, sessionUrl }: {
    peerConnection: RTCPeerConnection
    sessionUrl: string | null
}) => {
    let isStopped = false
    return async () => {
        if (isStopped) {
            return
        }
        isStopped = true
        peerConnection.close()
        if (sessionUrl) {
            await fetch(sessionUrl, { method: 'DELETE' })
        }
    }
}

const negotiateOrClose = async (params: { peerConnection: RTCPeerConnection; endpointUrl: string }) => {
    try {
        return await negotiateSession(params)
    } catch (error) {
        params.peerConnection.close()
        throw error
    }
}

/**
 * Publishes a camera/mic stream to a WHIP URL. Resolves once the server has answered; the connection itself
 * comes up shortly after (watch `onConnectionStateChange`).
 */
export const startVideoBroadcast = async ({ publishUrl, mediaStream, onConnectionStateChange }: {
    publishUrl: string
    mediaStream: MediaStream
    onConnectionStateChange?: (connectionState: TVideoConnectionState) => void
}) => {
    const peerConnection = createPeerConnection()
    const sendersByKind = new Map<string, RTCRtpSender>()
    const trackKinds = [ 'audio', 'video' ] as const
    trackKinds.forEach(kind => {
        const track = mediaStream.getTracks().find(mediaTrack => mediaTrack.kind === kind)
        const transceiver = peerConnection.addTransceiver(track ?? kind, {
            direction: 'sendonly',
            streams: [ mediaStream ],
        })
        sendersByKind.set(kind, transceiver.sender)
    })
    peerConnection.addEventListener('connectionstatechange', () => {
        onConnectionStateChange?.(peerConnection.connectionState)
    })

    const sessionUrl = await negotiateOrClose({ peerConnection, endpointUrl: publishUrl })

    /** Swaps the camera or mic mid-broadcast without renegotiating */
    const replaceTrack = async (track: MediaStreamTrack) => {
        const sender = sendersByKind.get(track.kind)
        if (!sender) {
            throw new Error(`replaceTrack: no ${track.kind} sender`)
        }
        await sender.replaceTrack(track)
    }

    return {
        stop: createSessionStopper({ peerConnection, sessionUrl }),
        replaceTrack,
    }
}

/** How often playback checks that the host's video frames are still arriving */
const VIDEO_FRAME_CHECK_INTERVAL_MS = 1000
/**
 * No new frames for this long means the host stopped broadcasting. Cloudflare keeps the session up and the
 * track never mutes, so without this the player would hold the last frame forever.
 */
const VIDEO_FRAME_SILENCE_MS = 3000

/** Frames decoded so far for the session's incoming video, or null before any video stats exist */
const getVideoFramesDecoded = async (peerConnection: RTCPeerConnection) => {
    const stats = await peerConnection.getStats()
    let framesDecoded: number | null = null
    stats.forEach((report: { type?: string; kind?: string; framesDecoded?: number }) => {
        if (report.type === 'inbound-rtp' && report.kind === 'video' && typeof report.framesDecoded === 'number') {
            framesDecoded = report.framesDecoded
        }
    })
    return framesDecoded
}

/**
 * Plays a WHEP URL. Resolves with a stream that fills with the host's tracks as they arrive.
 * `onVideoFlowChange` reports whether video frames are actually arriving: a session can be up while the
 * host isn't broadcasting, so it follows the track's mute events and, since those don't always fire,
 * whether the frame count is still going up.
 */
export const startVideoPlayback = async ({ playbackUrl, onConnectionStateChange, onVideoFlowChange }: {
    playbackUrl: string
    onConnectionStateChange?: (connectionState: TVideoConnectionState) => void
    onVideoFlowChange?: (isVideoFlowing: boolean) => void
}) => {
    const peerConnection = createPeerConnection()
    peerConnection.addTransceiver('video', { direction: 'recvonly' })
    peerConnection.addTransceiver('audio', { direction: 'recvonly' })

    let isVideoFlowing = false
    const reportVideoFlow = (isNowFlowing: boolean) => {
        if (isNowFlowing !== isVideoFlowing) {
            isVideoFlowing = isNowFlowing
            onVideoFlowChange?.(isNowFlowing)
        }
    }

    const mediaStream = new MediaStream()
    peerConnection.addEventListener('track', ({ track }) => {
        mediaStream.addTrack(track)
        if (track.kind !== 'video') {
            return
        }
        track.addEventListener('unmute', () => reportVideoFlow(true))
        track.addEventListener('mute', () => reportVideoFlow(false))
        if (!track.muted) {
            reportVideoFlow(true)
        }
    })

    let lastFramesDecoded: number | null = null
    let lastNewFrameAtMs = Date.now()
    const checkForNewFrames = async () => {
        const framesDecoded = await getVideoFramesDecoded(peerConnection)
        if (framesDecoded !== null && framesDecoded !== lastFramesDecoded) {
            lastFramesDecoded = framesDecoded
            lastNewFrameAtMs = Date.now()
            reportVideoFlow(true)
        } else if (Date.now() - lastNewFrameAtMs >= VIDEO_FRAME_SILENCE_MS) {
            reportVideoFlow(false)
        }
    }
    const frameCheckIntervalId = setInterval(() => {
        // Stats can fail once the connection closes; the next check (or stop) settles it
        checkForNewFrames().catch(() => undefined)
    }, VIDEO_FRAME_CHECK_INTERVAL_MS)

    peerConnection.addEventListener('connectionstatechange', () => {
        if (peerConnection.connectionState === 'closed') {
            clearInterval(frameCheckIntervalId)
        }
        onConnectionStateChange?.(peerConnection.connectionState)
    })

    let sessionUrl: string | null
    try {
        sessionUrl = await negotiateOrClose({ peerConnection, endpointUrl: playbackUrl })
    } catch (error) {
        clearInterval(frameCheckIntervalId)
        throw error
    }
    const stopSession = createSessionStopper({ peerConnection, sessionUrl })

    return {
        mediaStream,
        stop: () => {
            clearInterval(frameCheckIntervalId)
            return stopSession()
        },
    }
}
